import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Avatar,
  FileButton,
  Text,
  Box,
  Alert,
} from "@mantine/core";
import { IconUpload, IconAlertCircle, IconTrash } from "@tabler/icons-react";

const EditRepTypeModal = ({ opened, onClose, repType, onSave, onDelete }) => {
  const [name, setName] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const resetRef = useRef();

  useEffect(() => {
    if (opened && repType) {
      setName(repType.name || "");
      setImagePreview(repType.image || "");
      setImage(null);
      setError("");
    } else if (opened) {
      // For creating new rep types
      setName("");
      setImagePreview("");
      setImage(null);
      setError("");
    }
  }, [opened, repType]);

  const handleImageSelect = file => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image file size should be less than 5MB");
        return;
      }

      setImage(file);
      setError("");

      // Create preview URL
      const reader = new FileReader();
      reader.onload = e => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Rep type name is required");
      return;
    }

    // Always require an image for both new and existing rep types
    if (!image && !imagePreview) {
      setError("An image is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updatedRepType = {
        ...repType,
        name: name.trim(),
        image: image,
        imageUrl: imagePreview,
      };

      await onSave(updatedRepType);
    } catch (err) {
      setError(err.message || "Failed to save rep type");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
    setError(""); // Clear any existing errors
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await onDelete(repType.id);
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete rep type");
      setShowDeleteConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleClose = () => {
    setName("");
    setImage(null);
    setImagePreview("");
    setError("");
    setShowDeleteConfirmation(false);
    if (resetRef.current) {
      resetRef.current();
    }
    onClose();
  };

  const isNew = !repType || !repType.id;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isNew ? "Add Rep Type" : "Edit Rep Type"}
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Box ta="center">
          <Avatar
            src={imagePreview}
            size="xl"
            radius="xl"
            style={{
              margin: "0 auto",
              border: "2px solid #e0e0e0",
            }}
          >
            {name?.charAt(0)?.toUpperCase() || "R"}
          </Avatar>
        </Box>

        <FileButton
          resetRef={resetRef}
          onChange={handleImageSelect}
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        >
          {props => (
            <Button
              {...props}
              variant="light"
              leftSection={<IconUpload size={16} />}
              fullWidth
            >
              {image ? "Change Image" : "Upload Image *"}
            </Button>
          )}
        </FileButton>

        {image && (
          <Text size="sm" c="dimmed" ta="center">
            Selected: {image.name}
          </Text>
        )}

        <TextInput
          label="Rep Type Name"
          placeholder="Enter rep type name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          error={!name.trim() && error ? "Name is required" : null}
        />

        {showDeleteConfirmation && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                Are you sure you want to delete "{repType?.name}"? This action
                cannot be undone.
              </Text>
              <Group gap="sm">
                <Button
                  size="xs"
                  color="red"
                  onClick={handleDeleteConfirm}
                  loading={loading}
                >
                  Delete
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" gap="sm">
          {!isNew && onDelete && !showDeleteConfirmation && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteClick}
              disabled={loading}
            >
              Delete
            </Button>
          )}

          <Group justify="flex-end" gap="sm" style={{ marginLeft: "auto" }}>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim() || showDeleteConfirmation}
            >
              {isNew ? "Add Rep Type" : "Save Changes"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditRepTypeModal;
